import { inject } from '@angular/core';
import {
	CanActivateFn,
	CanActivateChildFn,
	Router,
	ActivatedRouteSnapshot,
	RouterStateSnapshot,
} from '@angular/router';
import { AuthService } from './auth.service';
import { Routing } from '@enums/routing.enum';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

/**
 * Guard that protects routes requiring authentication.
 * Redirects to login page if user is not authenticated.
 *
 * Modern implementation: Returns Observable<boolean> for async token refresh
 * instead of subscribing inside the guard (which doesn't work properly).
 */
export const authGuard: CanActivateFn = (
	_route: ActivatedRouteSnapshot,
	state: RouterStateSnapshot,
): boolean | Observable<boolean> => {
	const authService = inject(AuthService);
	const router = inject(Router);

	if (authService.isAuthenticated()) {
		// A confirmed session (access token already in memory) → activate immediately.
		if (authService.accessToken()) {
			return true;
		}

		// Optimistic boot state: a refresh token survived the reload but no access token has been
		// obtained yet. Confirm the session with ONE silent refresh BEFORE activating the protected
		// route. Without this, the guard passes optimistically, the logged-in shell renders, then a
		// stale token fails on the first API call and we bounce to login — the "flash of logged-in"
		// the user reported. Deferring activation until the refresh resolves removes that flash.
		return authService.refreshToken().pipe(
			map(() => true),
			catchError(() => {
				// refreshToken() clears auth state on a genuine 401/403. Transient errors (429/5xx/
				// network) keep the optimistic session — let it through so the interceptor can retry
				// later rather than falsely logging the user out.
				if (authService.isAuthenticated()) {
					return of(true);
				}
				router.navigate([Routing.PanelLogin], { queryParams: { returnUrl: state.url } });
				return of(false);
			}),
		);
	}

	// Not authenticated, redirect to login with return URL
	router.navigate([Routing.PanelLogin], {
		queryParams: { returnUrl: state.url },
	});
	return false;
};

/**
 * Guard for child routes - same logic as authGuard
 */
export const authGuardChild: CanActivateChildFn = (
	childRoute: ActivatedRouteSnapshot,
	state: RouterStateSnapshot,
) => {
	return authGuard(childRoute, state);
};

/**
 * Guard that prevents authenticated users from accessing public auth pages.
 * Redirects to dashboard if user is already authenticated.
 */
export const noAuthGuard: CanActivateFn = () => {
	const authService = inject(AuthService);
	const router = inject(Router);

	if (authService.isAuthenticated()) {
		// Already authenticated, redirect to dashboard
		router.navigate([Routing.PanelDashboard]);
		return false;
	}

	return true;
};

/**
 * Guard that requires email verification.
 * Use after authGuard to ensure user is authenticated.
 */
export const emailVerifiedGuard: CanActivateFn = () => {
	const authService = inject(AuthService);
	const router = inject(Router);

	const user = authService.user();

	if (!user) {
		router.navigate([Routing.PanelLogin]);
		return false;
	}

	if (!user.is_verified) {
		// Redirect to email verification page or show message
		router.navigate([Routing.PanelAccountSettings], {
			queryParams: { verify: 'email' },
		});
		return false;
	}

	return true;
};

/**
 * Guard that requires 2FA to be enabled.
 * Use after authGuard to ensure user is authenticated.
 */
export const requires2FAGuard: CanActivateFn = () => {
	const authService = inject(AuthService);
	const router = inject(Router);

	const user = authService.user();

	if (!user) {
		router.navigate([Routing.PanelLogin]);
		return false;
	}

	if (!user.totp_enabled) {
		// Redirect to 2FA setup page
		router.navigate([Routing.PanelAccountSettings], {
			queryParams: { setup: '2fa' },
		});
		return false;
	}

	return true;
};

/**
 * Guard that requires admin role.
 * Use after authGuard to ensure user is authenticated.
 * Redirects to dashboard if user is not an admin.
 */
export const adminGuard: CanActivateFn = () => {
	const authService = inject(AuthService);
	const router = inject(Router);

	const user = authService.user();

	if (!user) {
		router.navigate([Routing.PanelLogin]);
		return false;
	}

	if (user.role !== 'admin') {
		router.navigate([Routing.PanelDashboard]);
		return false;
	}

	return true;
};
